import { html, TemplateResult, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import "@/components/feedback/feedback_test";
import { renderMessage, renderMessages } from "@/feedback/message_helpers";
import type { DiffStyle, FeedbackEnvelopeContext, FileEntry, Testcase } from "@/types/FeedbackResult";

/**
 * Renders one testcase (group of tests) within a context.
 * Displays an optional description and the list of `d-feedback-test` children.
 *
 * @element d-feedback-testcase
 *
 * @prop {Testcase}                testcase          - Testcase data (tests, messages, description, …).
 * @prop {string}                  diff-style        - Forwarded to child tests; "split" or "unified".
 * @prop {FeedbackEnvelopeContext} submissionContext - Submission context (access token, activity path).
 */
@customElement("d-feedback-testcase")
export class FeedbackTestcase extends DodonaElement {
    @property({ type: Object }) testcase!: Testcase;
    @property({ type: String }) diffStyle: DiffStyle = "split";
    @property({ type: Object }) submissionContext: FeedbackEnvelopeContext = {};
    @property({ type: Object }) contextFiles: Record<string, FileEntry> | null = null;

    /**
     * Memoized merge of context- and testcase-level files. Recomputed in
     * `willUpdate` only when its inputs change, so the object identity stays
     * stable across unrelated re-renders.
     */
    @state() private files: Record<string, FileEntry> | null = null;

    override willUpdate(changedProperties: PropertyValues): void {
        super.willUpdate(changedProperties);

        if (!changedProperties.has("testcase") && !changedProperties.has("contextFiles")) {
            return;
        }

        const merged = { ...this.contextFiles, ...this.testcase?.data?.files };
        this.files = Object.keys(merged).length > 0 ? merged : null;
    }

    render(): TemplateResult {
        const tc = this.testcase;
        if (!tc) return html``;

        const statusClass = tc.accepted ? "correct" : "wrong";
        const resolvedFiles = this.files;

        return html`
            <div class="testcase ${statusClass}" data-lit>
                <div class="description">
                    <div class="indicator">
                        ${
                            tc.accepted
                                ? html`<i class="mdi mdi-check mdi-18" aria-hidden="true"></i>`
                                : html`<i class="mdi-close mdi mdi-18" aria-hidden="true"></i>`
                        }
                    </div>
                    ${tc.description ? renderMessage(tc.description, this.submissionContext, resolvedFiles) : ""}
                </div>
                ${(tc.tests ?? []).map(
                    (test) =>
                        html`<d-feedback-test
                            .test=${test}
                            .diffStyle=${this.diffStyle}
                            .submissionContext=${this.submissionContext}
                            .files=${resolvedFiles}
                        ></d-feedback-test>`,
                )}
                ${tc.messages?.length ? renderMessages(tc.messages, this.submissionContext, resolvedFiles) : ""}
            </div>
        `;
    }
}
